<script setup lang="ts">
// State timeline for one entity over the current window (Grafana state-timeline
// convention): patterned by state so colour is not the only cue, hover shows a
// tooltip with every event in the bucket, click opens the event dialog. `overlay` events (for example the
// destinations of an encode) are drawn as markers on top, so an encode row
// shows when several of its destinations failed at the same moment.
//
// The bar is divided into fixed buckets (a tenth of the window: 1 min at
// 10 min, 6 min at 1 h, 2.4 h at 24 h). Hover highlights the bucket under the
// cursor and summarises every event in it; click opens them all. A brief
// error is therefore never a one-pixel target: a state is drawn at least
// 4 px wide, problems over the good states beside them, and the ticks reach
// out of the bar so they show over any state.
import { computed, ref } from 'vue'
import type { Config, Event } from '../api'
import { useNode } from '../store'
import { clock, dotClass, dur, entityLabel, inWindow, notable, segClass, segments, stateRank, tipPos } from '../util'
import { useTicks } from '../ticks'
const ticks = useTicks()

const BUCKETS = 10 // a tenth of the window each: easy to hit, the same width on screen at any span
// `events` overrides the store lookup (the fleet page passes a node's online/offline events)
const props = defineProps<{
    kind: 'input' | 'encode' | 'destination' | 'output' | 'machine' | 'node' | 'storage'
    id: string
    current: string
    overlay?: Event[]
    events?: Event[]
    cfg?: Config
    loading?: boolean // the events are still coming: a placeholder, not an empty bar
}>()
// a bar drawn from the store follows its loading; one given its events, the caller's
const busy = computed(() => props.loading ?? (!props.events && (node.live ? node.eventsLoading : node.windowLoading)))
const own = () =>
    props.events ? props.events.filter((e) => e.type === 'state') : node.stateEvents(props.kind, props.id)
const emit = defineEmits<{ open: [{ from: number; to: number }] }>()
const node = useNode()

interface Bucket {
    i: number
    from: number
    to: number
}
function bucketOf(i: number): Bucket {
    const [start, end] = node.span
    const w = (end - start) / BUCKETS
    return { i, from: start + i * w, to: start + (i + 1) * w }
}
function bucketAt(e: MouseEvent): Bucket {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
    return bucketOf(Math.min(BUCKETS - 1, Math.max(0, Math.floor(((e.clientX - r.left) / r.width) * BUCKETS))))
}
function clickAt(e: MouseEvent) {
    const b = bucketAt(e)
    emit('open', { from: b.from, to: b.to })
}
// the keyboard (WEB-36): focused, ← → pick a stretch (its tip shows, as
// the mouse's), Home and End the first and the latest, Enter opens it
function onKey(e: KeyboardEvent) {
    const el = e.currentTarget as HTMLElement
    const cur = tip.value?.bucket.i
    let i: number
    switch (e.key) {
        case 'ArrowLeft':
            i = Math.max(0, (cur ?? BUCKETS) - 1)
            break
        case 'ArrowRight':
            i = Math.min(BUCKETS - 1, (cur ?? -1) + 1)
            break
        case 'Home':
            i = 0
            break
        case 'End':
            i = BUCKETS - 1
            break
        case 'Enter':
        case ' ':
            e.preventDefault()
            if (cur !== undefined) emit('open', { from: tip.value!.bucket.from, to: tip.value!.bucket.to })
            return
        default:
            return
    }
    e.preventDefault()
    const r = el.getBoundingClientRect()
    showTip(bucketOf(i), {
        x: Math.min(r.left + ((i + 0.5) / BUCKETS) * r.width + 12, window.innerWidth - 380),
        y: Math.min(r.bottom + 6, window.innerHeight - 240),
    })
}

interface Seg {
    x: number
    w: number
    state: string
}
interface Mark {
    x: number
    ev: Event
}

// drawn worst last, so a short problem widened to its 4 px is never
// covered by the good state after it
const segs = computed<Seg[]>(() =>
    segments(own(), node.span[0], node.span[1], node.lostAt || Infinity).sort(
        (a, b) => (stateRank[a.state] ?? 0) - (stateRank[b.state] ?? 0),
    ),
)

const marks = computed<Mark[]>(() => {
    if (!props.overlay) return []
    const [start, end] = node.span
    return props.overlay
        .filter((e) => e.type === 'state' && e.severity !== 'info')
        .map((e) => ({ x: (Date.parse(e.ts) - start) / (end - start), ev: e }))
        .filter((m) => m.x >= 0 && m.x <= 1)
})

// Hover summarises the bucket under the cursor: every event of this entity
// and its overlay in it that the bar draws as anything but green (notable),
// grouped by message with counts, instead of one tooltip per event.
interface Group {
    msg: string
    n: number
    since?: number // began before the bucket: still going on in it
    state: string
    entity: string
}
interface Tip {
    x: number
    y: number
    bucket: Bucket
    groups: Group[]
    total: number
}
const tip = ref<Tip | null>(null)
function hover(e: MouseEvent) {
    showTip(bucketAt(e), tipPos(e))
}
function showTip(bucket: Bucket, pos: { x: number; y: number }) {
    // the bucket's events and the problems already going on when it starts
    // (a long no signal), as a click shows them
    const all = inWindow(
        // the entity's events before the window too: a no signal that began
        // before it is carried into the bucket (it showed in the bar, not here)
        [...(props.events ?? node.entityEvents(props.kind, props.id)), ...(props.overlay ?? [])],
        () => true,
        bucket,
    ).filter(notable)
    const byMsg = new Map<string, Group>()
    let total = 0
    for (const ev of all) {
        const ts = Date.parse(ev.ts)
        total++
        const key = ev.kind + ev.entity + ev.message
        const g = byMsg.get(key)
        if (g) g.n++
        else
            byMsg.set(key, {
                msg: ev.message,
                n: 1,
                since: ts < bucket.from ? ts : undefined,
                state: ev.state || (ev.severity === 'error' ? 'error' : 'starting'),
                entity:
                    ev.kind === props.kind && ev.entity === props.id
                        ? ''
                        : entityLabel(props.cfg ?? node.config, ev.kind, ev.entity),
            })
    }
    const groups = [...byMsg.values()].sort((a, b) => b.n - a.n).slice(0, 8)
    tip.value = { ...pos, bucket, groups, total }
}
const cls = (state: string) => `absolute inset-y-0 ${segClass(state)}`
</script>

<template>
    <div
        v-if="busy"
        class="relative h-2.5 overflow-hidden rounded-xs bg-bg-3 motion-reduce:animate-pulse-dot"
        role="progressbar"
        aria-busy="true"
        aria-label="loading the timeline"
    >
        <div
            class="absolute inset-y-0 w-[30%] animate-indet bg-linear-to-r from-transparent via-fg/25 to-transparent motion-reduce:hidden"
        />
    </div>
    <div
        v-else
        class="relative h-2.5 cursor-pointer rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        tabindex="0"
        role="button"
        :aria-label="`${kind} timeline: arrow keys pick a stretch, Enter shows its events`"
        @mousemove="hover"
        @mouseleave="tip = null"
        @blur="tip = null"
        @click.stop="clickAt"
        @keydown="onKey"
    >
        <div class="absolute inset-0 overflow-hidden rounded-xs bg-bg-3">
            <div
                v-for="(s, i) in segs"
                :key="i"
                :class="cls(s.state)"
                :style="{
                    left: `min(${s.x * 100}%, calc(100% - 4px))`,
                    width: `max(${s.w * 100}%, 4px)`,
                }"
            />
            <div
                v-for="(m, i) in marks"
                :key="'m' + i"
                class="pointer-events-none absolute -inset-y-px -ml-0.5 w-1 outline outline-bg-1"
                :class="m.ev.severity === 'warning' ? 'bg-warn' : 'bg-bad'"
                :style="{ left: `min(${m.x * 100}%, calc(100% - 2px))` }"
            />
            <div
                v-if="tip"
                class="pointer-events-none absolute inset-y-0 bg-white/28 outline -outline-offset-1 outline-fg light:bg-black/18"
                :style="{ left: (tip.bucket.i / BUCKETS) * 100 + '%', width: 100 / BUCKETS + '%' }"
            />
        </div>
        <!-- the ticks reach 2 px out of the bar, over every state -->
        <div
            v-for="(t, i) in ticks"
            :key="'t' + i"
            class="pointer-events-none absolute -inset-y-0.5 w-px"
            :class="t.major ? 'bg-white/55 light:bg-black/50' : 'bg-white/35 light:bg-black/30'"
            :style="{ left: t.x * 100 + '%' }"
        />
        <Teleport to="body">
            <div
                v-if="tip"
                class="pointer-events-none fixed z-30 max-w-[360px] rounded-md border border-line-strong bg-bg-3 px-2 py-1.5 text-xs"
                :style="{ left: tip.x + 'px', top: tip.y + 'px' }"
            >
                <div class="text-xs">
                    <b>{{ clock(tip.bucket.from, true) }} – {{ clock(tip.bucket.to, true) }}</b>
                    <span class="text-fg-2"
                        >· {{ dur((tip.bucket.to - tip.bucket.from) / 1000) }} ·
                        {{ tip.total ? `${tip.total} event${tip.total > 1 ? 's' : ''}` : 'no events' }}</span
                    >
                </div>
                <div v-for="(g, i) in tip.groups" :key="i" class="flex items-center gap-1.5 text-xs">
                    <span :class="dotClass(g.state)" /><span v-if="g.since" class="text-fg-2"
                        >since {{ clock(g.since, true) }}</span
                    ><span v-else class="font-mono tabular-nums">{{ g.n }}×</span>
                    <span class="truncate"
                        ><span v-if="g.entity" class="text-fg-2">{{ g.entity }} · </span>{{ g.msg }}</span
                    >
                </div>
                <div v-if="tip.total > tip.groups.reduce((a, g) => a + g.n, 0)" class="text-xs text-fg-3">
                    … click for all
                </div>
                <div v-else-if="tip.total" class="text-xs text-fg-3">click for details</div>
            </div>
        </Teleport>
    </div>
</template>
